import { useMemo, useState } from "react";
import { Badge, Button, FlexboxGrid, Input, Modal, Panel, Stack, Table, Tag, Tooltip, Whisper } from "rsuite";
import { formatCpfCnpj } from "../../../core/utils/formatCpfCnpj";

const { Column, HeaderCell, Cell } = Table;

export type OmieErrorRecord = {
    scope: "cliente" | "conta";
    integrationKey?: string;
    endpoint?: string;
    status?: number;
    fault?: string;
    message?: string;
    ref?: {
        rowInSheet?: number;
        cpfcnpj?: string;
        codigo_lancamento_integracao?: string;
        numero_documento?: string;
        kind?: "CR" | "CP";
    };
};

export type ImportErrors = {
    clients?: OmieErrorRecord[];
    accounts?: OmieErrorRecord[];
};

type Props = {
    open: boolean;
    errors: ImportErrors | null;
    onClose: () => void;
};

export default function ImportErrorsModal({ open, errors, onClose }: Props) {
    const [query, setQuery] = useState("");

    const flatRows = useMemo(() => {
        const rows = [
            ...(errors?.clients ?? []).map((e, idx) => ({ id: `c-${idx}`, ...e })),
            ...(errors?.accounts ?? []).map((e, idx) => ({ id: `a-${idx}`, ...e })),
        ];
        if (!query.trim()) return rows;
        const q = query.toLowerCase();
        return rows.filter((r) => {
            const text = [
                r.scope,
                r.integrationKey,
                r.endpoint,
                r.status,
                r.fault,
                r.message,
                r.ref?.cpfcnpj,
                r.ref?.numero_documento,
                r.ref?.codigo_lancamento_integracao,
                r.ref?.kind,
                r.ref?.rowInSheet,
            ]
                .filter((v) => v !== undefined && v !== null)
                .join(" ")
                .toString()
                .toLowerCase();
            return text.includes(q);
        });
    }, [errors, query]);

    const totalClients = errors?.clients?.length ?? 0;
    const totalAccounts = errors?.accounts?.length ?? 0;
    const total = totalClients + totalAccounts;

    async function handleExportExcel() {
        const rows = flatRows as (OmieErrorRecord & { id: string })[];

        const headers = [
            { key: "scope", label: "Escopo" },
            { key: "rowInSheet", label: "Linha" },
            { key: "kind", label: "Tipo" },
            { key: "fault", label: "Mensagem de Erro" },
        ];

        const data = rows.map(r => ({
            scope: r.scope,
            rowInSheet: r.ref?.rowInSheet ?? "",
            kind: r.ref?.kind ?? "",
            fault: r.fault ?? r.message ?? "",
        }));

        const XLSX = await import("xlsx");

        const aoa = [
            headers.map(h => h.label),
            ...data.map(row => headers.map(h => (row as any)[h.key])),
        ];

        const ws = XLSX.utils.aoa_to_sheet(aoa);
        ws["!cols"] = [{ wch: 10 }, { wch: 8 }, { wch: 8 }, { wch: 80 }];

        const range = {
            s: { r: 0, c: 0 },
            e: { r: aoa.length - 1, c: headers.length - 1 },
        };
        ws["!autofilter"] = { ref: XLSX.utils.encode_range(range) };
        ws["!rows"] = [{ hpt: 22 }];
        (ws as any)["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2" };

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Erros");
        XLSX.writeFile(wb, `import-errors-${Date.now()}.xlsx`);
    }

    return (
        <Modal open={open} onClose={onClose} size="full">
            <Modal.Header>
                <Modal.Title>Erros da Importação</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Stack justifyContent="space-between" alignItems="center" wrap spacing={12} style={{ marginBottom: 12, padding: 6 }}>
                    <Stack spacing={8} alignItems="center">
                        <Tag color="red">Total: {total}</Tag>
                        <Tag color="orange">Clientes: {totalClients}</Tag>
                        <Tag color="violet">Contas: {totalAccounts}</Tag>
                    </Stack>

                    <Stack spacing={8} alignItems="center">
                        <Input placeholder="Pesquisar..." value={query} onChange={setQuery} style={{ width: 320 }} />
                        <Whisper placement="bottom" speaker={<Tooltip>Exportar Excel com os erros</Tooltip>}>
                            <Button appearance="primary" onClick={handleExportExcel}>
                                Baixar Excel
                            </Button>
                        </Whisper>
                        {/* <Whisper placement="bottom" speaker={<Tooltip>Exportar JSON com os erros</Tooltip>}>
                            <Button
                                appearance="ghost"
                                onClick={() => {
                                    const blob = new Blob([JSON.stringify(errors ?? {}, null, 2)], { type: "application/json" });
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement("a");
                                    a.href = url;
                                    a.download = `import-errors-${Date.now()}.json`;
                                    a.click();
                                    URL.revokeObjectURL(url);
                                }}
                            >
                                Baixar JSON
                            </Button>
                        </Whisper> */}
                    </Stack>
                </Stack>

                <Panel bordered header="Detalhes dos erros" defaultExpanded>
                    <Table height={350} data={flatRows} rowKey="id" bordered cellBordered hover virtualized affixHeader>
                        <Column width={96} fixed>
                            <HeaderCell>Escopo</HeaderCell>
                            <Cell dataKey="scope">
                                {(row: OmieErrorRecord) =>
                                    row.scope === "conta" ? <Tag color="violet">conta</Tag> : <Tag color="orange">cliente</Tag>
                                }
                            </Cell>
                        </Column>

                        <Column width={110}>
                            <HeaderCell>Linha</HeaderCell>
                            <Cell>{(row: OmieErrorRecord) => row.ref?.rowInSheet ?? "-"}</Cell>
                        </Column>

                        <Column width={160}>
                            <HeaderCell>CPF/CNPJ</HeaderCell>
                            <Cell>{(row: OmieErrorRecord) => (row.ref?.cpfcnpj ? formatCpfCnpj(row.ref?.cpfcnpj) : "-")}</Cell>
                        </Column>

                        <Column width={800} resizable>
                            <HeaderCell>Erro</HeaderCell>
                            <Cell>{(row: OmieErrorRecord) => row.fault || row.message || "-"}</Cell>
                        </Column>
                    </Table>
                </Panel>
            </Modal.Body>

            <Modal.Footer>
                <FlexboxGrid justify="end">
                    <Button appearance="primary" onClick={onClose}>
                        Fechar
                    </Button>
                </FlexboxGrid>
            </Modal.Footer>
        </Modal>
    );
}
